import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Cpu, Gamepad2, Layers, Code, Database, Zap } from "lucide-react";

// --- Helper Components ---

// StarRating Helper
const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`h-4 w-4 ${
          i <= rating ? "text-yellow-500" : "text-gray-300" 
        } inline-block`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
};

// Verified Badge
const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 ml-2 border border-gray-300">
    <svg
      className="w-3 h-3 mr-1 text-blue-700"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
    Verified
  </span>
);

// --- Tools Section (C++ Specific) ---
const ToolsYouWillMaster = () => {
  const tools = [
    { name: 'C++', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/cplusplus/cplusplus-original.svg' },
    { name: 'VS Code', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vscode/vscode-original.svg' },
    { name: 'Visual Studio', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/visualstudio/visualstudio-original.svg' },
    { name: 'G++', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/18/ISO_C%2B%2B_Logo.svg/1200px-ISO_C%2B%2B_Logo.svg.png' }, // Generic C++ Compiler logo representation
    { name: 'Git', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/git/git-original.svg' },
    { name: 'CMake', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/cmake/cmake-original.svg' },
    { name: 'Code::Blocks', logo: 'https://upload.wikimedia.org/wikipedia/commons/d/d2/Codeblocks.svg' },
    { name: 'Unreal Engine', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/unrealengine/unrealengine-original.svg' }, // Mentioning for Gaming scope
  ];

  return (
    <section className="py-20 bg-gray-100">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-12 text-black font-aligran">
          Tools You Will <span className="text-blue-700">Master</span>
        </h2>
        
        <div className="grid grid-cols-4 md:grid-cols-8 gap-8 mb-8">
          {tools.map((tool, i) => (
            <motion.div
              key={i}
              className="flex flex-col items-center justify-center p-4 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
            >
              <img
                src={tool.logo}
                alt={`${tool.name} Logo`}
                className="w-16 h-16 mb-2 object-contain"
              />
              <p className="text-sm font-semibold text-black mt-2">{tool.name}</p>
            </motion.div>
          ))}
        </div>
        
        <p className="text-gray-600 max-w-2xl mx-auto">
          We use industry-standard IDEs like <b>Visual Studio</b> and <b>VS Code</b>, and introduce you to <b>CMake</b> for building complex projects.
        </p>
      </div>
    </section>
  );
};

const CppLanguage = () => {
  // --- Data ---
  const testimonials = [
    {
      id: 1,
      name: "Saurabh Mishra",
      review:
        "The OOPs concepts (Polymorphism, Inheritance) were explained with real-world examples. It helped me clear my interview at a top product-based company.",
      rating: 5,
      date: "2024-08-20",
      profileImg: "https://randomuser.me/api/portraits/men/22.jpg", 
      source: "LinkedIn",
    },
    {
      id: 2,
      name: "Tanya Roy",
      review:
        "DSA used to scare me, but the C++ STL module made implementation so easy. Vectors and Maps are now my favorite tools for competitive coding.",
      rating: 5,
      date: "2024-08-12",
      profileImg: "https://randomuser.me/api/portraits/women/28.jpg", 
      source: "Google Reviews",
    },
    {
      id: 3,
      name: "Rahul Tyagi",
      review:
        "Best course for transitioning from C to C++. The focus on Constructors and Memory Management is exactly what I needed for Game Development.",
      rating: 4,
      date: "2024-08-05",
      profileImg: "https://randomuser.me/api/portraits/men/33.jpg", 
      source: "Website Feedback",
    },
    {
      id: 4,
      name: "Priya Bansal",
      review:
        "We built a 'Bank Management System' using File Handling and Classes. It was a complete hands-on experience.",
      rating: 5,
      date: "2024-07-28",
      profileImg: "https://randomuser.me/api/portraits/women/55.jpg",
      source: "Google Reviews",
    },
    {
      id: 5,
      name: "Aditya Singh",
      review:
        "Great faculty! They covered advanced topics like Templates and Exception Handling which most institutes skip.",
      rating: 4,
      date: "2024-07-15",
      profileImg: "https://randomuser.me/api/portraits/men/60.jpg",
      source: "LinkedIn",
    },
  ];

  const faqContent = [
    {
      title: "How is C++ different from C?",
      answer:
        "C++ is an extension of C. The main difference is that C++ supports Object-Oriented Programming (Classes & Objects), making it better for large, complex software.",
    },
    {
      title: "Is C++ important for Data Structures (DSA)?",
      answer:
        "Yes! C++ is the most popular language for DSA and Competitive Programming because of its speed and the powerful Standard Template Library (STL).",
    },
    {
      title: "Can I use C++ for Game Development?",
      answer:
        "Absolutely. C++ is the industry standard for high-performance games (used in Unreal Engine). We cover the foundational concepts required for it.",
    },
    {
      title: "Do I need to know C before C++?",
      answer:
        "Not strictly, but it helps. Our course covers the basics of C syntax in the first module, so you can start C++ directly as well.",
    },
    {
      title: "What is STL?",
      answer:
        "STL (Standard Template Library) provides ready-to-use classes for Data Structures like Vectors, Lists, and Stacks. It saves time and is crucial for coding tests.",
    },
  ];

  const curriculumContent = [
    {
      title: "Module 1: Introduction & C vs C++",
      description: "Structure of C++, Input/Output streams (cin/cout), Namespaces, and basic syntax differences from C.",
    },
    {
      title: "Module 2: Control Flow & Functions",
      description: "Loops, Decision making, Functions, Default Arguments, and Inline Functions.",
    },
    {
      title: "Module 3: Pointers & References",
      description: "Understanding Pointers, Reference variables, Dynamic Memory Allocation (new/delete operators).",
    },
    {
      title: "Module 4: OOPs - Classes & Objects",
      description: "The core of C++. Defining Classes, creating Objects, Access Specifiers (Public, Private, Protected).",
    },
    {
      title: "Module 5: Constructors & Destructors",
      description: "Default, Parameterized, and Copy Constructors. Understanding Object life cycle and Destructors.",
    },
    {
      title: "Module 6: Inheritance & Polymorphism",
      description: "Single, Multiple, Multi-level Inheritance. Function Overloading and Virtual Functions (Run-time Polymorphism).",
    },
    {
      title: "Module 7: STL (Standard Template Library)",
      description: "Mastering Vectors, Lists, Maps, Sets, and Iterators for efficient coding and DSA.",
    },
    {
      title: "Module 8: File Handling & Exception Handling",
      description: "Reading/Writing files using fstream. Try-Catch blocks for error handling in robust applications.",
    },
  ];

  // Highlights Data with C++ Specific Images
  const highlights = [
    { 
      title: 'OOPs Mastery', 
      desc: 'Master Classes, Objects & Inheritance.', 
      img: 'https://images.unsplash.com/photo-1555099962-4199c345e5dd?q=80&w=2070' // Code Structure
    },
    { 
      title: 'DSA with STL', 
      desc: 'Vectors, Maps, Sets & Algorithms.', 
      img: 'https://images.unsplash.com/photo-1504639725590-34d0984388bd?q=80&w=2067' // Complexity/Code
    },
    { 
      title: 'Gaming Logic', 
      desc: 'Foundation for Unreal Engine.', 
      img: 'https://images.unsplash.com/photo-1552820728-8b83bb6b773f?q=80&w=2070' // Gaming
    },
    { 
      title: 'Competitive Code', 
      desc: 'Crack coding interviews easily.', 
      img: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?q=80&w=2070' // Laptop/Coding
    },
    { 
      title: 'High Performance', 
      desc: 'Write blazing fast applications.', 
      img: 'https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=2070' // Chip/Speed
    },
    { 
      title: 'Memory Mgmt', 
      desc: 'Master "new" and "delete".', 
      img: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=2070' // Memory/Hardware
    },
    { 
      title: 'Expert Faculty', 
      desc: 'Learn from Senior Software Engineers.', 
      img: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?q=80&w=2070' // Classroom
    },
    { 
      title: 'Certification', 
      desc: 'C++ Programming Certification.', 
      img: 'https://images.unsplash.com/photo-1589330273594-fade1ee91647?q=80&w=2070' // Certificate
    },
    { 
      title: 'Placement', 
      desc: 'Mock interviews for product companies.', 
      img: 'https://images.unsplash.com/photo-1521791136064-7984c1bc8179?q=80&w=2070' // Interview
    },
  ];

  // --- Logic for Carousel ---
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setVisibleCount(3);
      else if (window.innerWidth >= 768) setVisibleCount(2);
      else setVisibleCount(1);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const autoScrollInterval = setInterval(() => {
      setCurrentTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(autoScrollInterval);
  }, [testimonials.length]);

  const currentVisibleTestimonials = [];
  for (let i = 0; i < visibleCount; i++) {
    currentVisibleTestimonials.push(
      testimonials[(currentTestimonialIndex + i) % testimonials.length]
    );
  }

  // --- Logic for Form ---
  const [formData, setFormData] = useState({ firstName: "", businessEmail: "", phone: "" });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert("Thank you! We will contact you shortly regarding the C++ course.");
    setFormData({ firstName: "", businessEmail: "", phone: "" });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="bg-white text-gray-900 font-sans">
      <Helmet>
        <title>C++ Programming Course | OOPs, DSA & STL</title>
        <meta name="description" content="Master C++ from Core to Advanced. Learn OOPs, STL, and Data Structures for placements." />
      </Helmet>

      {/* --- HERO SECTION --- */}
      <section className="relative overflow-hidden h-[550px] flex items-center bg-black"> 
      
      {/* Background Image + Gradient */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1517134191118-9d595e4c8c2b?q=80&w=2070&auto=format&fit=crop"
          alt="C++ Coding"
          className="w-full h-full object-cover opacity-30"
        />
        {/* Deep Blue Gradient for C++ Theme */}
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-blue-800/40" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-center w-full pt-20">

        {/* Left Content */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-block px-3 py-1 mb-4 border border-blue-500 rounded-full bg-blue-900/30 text-blue-400 text-sm font-semibold tracking-wide">
            HIGH PERFORMANCE
          </div>

          <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-6 text-gray-100 font-aligran">
            Master C++ Programming
            <span className="text-blue-500 block">Core to Advanced</span>
          </h1>

          <p className="text-xl text-gray-300 max-w-2xl mb-8">
            The language of choice for <strong>Competitive Programming</strong>, <strong>Game Dev</strong>, and High-Performance Applications.
          </p>

          <button
            onClick={() =>
              document.getElementById("enquiry-form")?.scrollIntoView({ behavior: "smooth" })
            }
            className="bg-blue-700 hover:bg-blue-800 text-white font-bold py-4 px-10 rounded-lg shadow-[0_5px_20px_rgba(29,78,216,0.3)] transition duration-300 transform hover:scale-105"
          >
            Start Learning
          </button>
        </motion.div>

        {/* Right Side Image (C++ Logo) */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9 }}
          className="flex justify-center md:justify-end"
        >
          <img
            src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/cplusplus/cplusplus-original.svg" 
            alt="C++ Language Logo"
            className="w-64 md:w-80 
                       drop-shadow-[0_5px_30px_rgba(37,99,235,0.4)]
                       transition-transform duration-300 
                       hover:scale-105"
          />
        </motion.div>
      </div>
      </section>

      {/* --- WHY LEARN SECTION --- */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6 text-center">

          <h2 className="text-4xl md:text-5xl font-extrabold mb-4 text-gray-900 font-aligran">
            Why Learn <span className="text-blue-700">C++?</span>
          </h2>

          <p className="text-gray-600 mb-14 max-w-3xl mx-auto text-lg leading-relaxed">
            It combines the power of C with modern Object-Oriented features, making it essential for top-tier software roles.
          </p>

          <div className="grid md:grid-cols-3 gap-8">

            <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
              <div className="w-14 h-14 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center mb-5 mx-auto group-hover:scale-110 transition">
                <Cpu size={28} />
              </div>
              <h3 className="font-bold text-2xl text-black mb-3">OOPs Power</h3>
              <p className="text-gray-600 leading-relaxed">
                Object-Oriented Programming allows you to model real-world problems, making code modular and reusable.
              </p>
            </div>

            <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
              <div className="w-14 h-14 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center mb-5 mx-auto group-hover:scale-110 transition">
                <Gamepad2 size={28} />
              </div>
              <h3 className="font-bold text-2xl text-black mb-3">Gaming & Graphics</h3>
              <p className="text-gray-600 leading-relaxed">
                The primary language for game engines like Unreal Engine. If you want to build games, learn C++.
              </p>
            </div>

            <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
              <div className="w-14 h-14 bg-green-100 text-green-700 rounded-full flex items-center justify-center mb-5 mx-auto group-hover:scale-110 transition">
                <Layers size={28} />
              </div>
              <h3 className="font-bold text-2xl text-black mb-3">STL & DSA</h3>
              <p className="text-gray-600 leading-relaxed">
                The Standard Template Library (STL) gives you powerful algorithms and data structures out of the box.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* --- HIGHLIGHTS SECTION --- */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-gray-900 font-aligran">
            Course <span className="text-blue-700">Highlights</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {highlights.map((item, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition overflow-hidden flex h-44 border-l-4 border-blue-700"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="w-1/2 p-5 flex flex-col justify-center">
                  <h3 className="font-bold text-lg text-black mb-1">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
                <div className="w-1/2 relative">
                  <img src={item.img} alt={item.title} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-l from-black/40 to-transparent"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- CURRICULUM SECTION --- */}
      <section className="py-20 bg-white"> 
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-left text-gray-900 font-aligran"> 
            Detailed <span className="text-blue-700">Curriculum</span>
          </h2>
          <div className="space-y-4">
            {curriculumContent.map((module, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50"> 
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-100 hover:bg-gray-200 transition flex justify-between items-center text-gray-900"> 
                    {module.title}
                    <span className="transition-transform group-open:rotate-180 text-gray-900">▼</span>
                  </summary>
                  <div className="px-6 py-4 text-black border-t border-gray-200 leading-relaxed"> 
                    <p>{module.description}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- TOOLS SECTION --- */}
      <ToolsYouWillMaster />

      {/* --- FAQ & FORM SECTION --- */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-10 bg-white"> 
        <div className="md:col-span-2">
          <h2 className="text-3xl font-bold mb-8 text-gray-900 font-aligran">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqContent.map((item, idx) => (
              <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg"> 
                <details className="group p-4">
                  <summary className="cursor-pointer font-medium text-gray-900 hover:text-gray-700 flex justify-between">
                    {item.title}
                    <span className="text-gray-900 group-open:text-blue-700">+</span>
                  </summary>
                  <p className="mt-4 text-black text-sm border-t border-gray-200 pt-3">{item.answer}</p> 
                </details>
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-1" id="enquiry-form">
          <div className="bg-gray-100 border border-blue-300 rounded-xl p-6 shadow-xl sticky top-10">
            <h3 className="text-2xl font-bold mb-2 text-center text-blue-700">Book Your Seat</h3>
            <p className="text-center text-gray-600 mb-6 text-sm">Join the C++ Cohort.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input type="text" name="firstName" placeholder="Your Name" required className="w-full bg-white border border-gray-300 rounded p-3 text-black focus:border-blue-700 focus:outline-none" value={formData.firstName} onChange={handleInputChange} />
              <input type="email" name="businessEmail" placeholder="Email Address" required className="w-full bg-white border border-gray-300 rounded p-3 text-black focus:border-blue-700 focus:outline-none" value={formData.businessEmail} onChange={handleInputChange} />
              <input type="tel" name="phone" placeholder="Phone Number" required className="w-full bg-white border border-gray-300 rounded p-3 text-black focus:border-blue-700 focus:outline-none" value={formData.phone} onChange={handleInputChange} />
              <button type="submit" className="w-full bg-gradient-to-r from-blue-700 to-blue-800 hover:from-blue-800 hover:to-blue-900 text-white font-bold py-3 rounded transition shadow-lg">ENQUIRE NOW</button>
              <p className="text-xs text-center text-gray-500 mt-2">We respect your privacy.</p>
            </form>
          </div>
        </div>
      </section>

      {/* --- TESTIMONIALS SECTION --- */}
      <section className="bg-gray-50 py-20 border-t border-gray-200"> 
        <div className="max-w-7xl mx-auto px-6 text-left"> 
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-gray-900 font-aligran">
                Success <span className="text-blue-700">Stories</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
                <AnimatePresence mode="popLayout">
                    {currentVisibleTestimonials.map((testimonial) => (
                        <motion.div
                            key={testimonial.id}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.4 }}
                            className="bg-white p-6 rounded-xl border border-gray-200 flex flex-col text-left hover:border-blue-400/50 transition shadow-sm"
                        >
                            <div className="flex items-center mb-4">
                                <img src={testimonial.profileImg} alt={testimonial.name} className="w-12 h-12 rounded-full border-2 border-blue-700 mr-3" />
                                <div>
                                    <h4 className="font-bold text-black">{testimonial.name}</h4> 
                                    <span className="text-gray-500 text-xs">{testimonial.source}</span>
                                </div>
                            </div>
                            <div className="mb-3"><StarRating rating={testimonial.rating} /></div>
                            <p className="text-gray-700 text-sm flex-grow">"{testimonial.review}"</p> 
                            <VerifiedBadge />
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
            <div className="flex justify-start mt-8 space-x-2"> 
                {testimonials.map((_, idx) => (
                    <button
                        key={idx}
                        onClick={() => setCurrentTestimonialIndex(idx)}
                        className={`h-2 w-2 rounded-full transition-all ${idx === currentTestimonialIndex ? 'bg-blue-700 w-6' : 'bg-gray-300'}`}
                    />
                ))}
            </div>
        </div>
      </section>
    </div>
  );
};

export default CppLanguage;